<template>
  <date-range-picker
    :key="localeKey"
    v-model="dateRange"
    :opens="opens"
    :timePicker="timePicker"
    :timePicker24Hour="timePicker24Hour"
    :time-picker-increment="1"
    :showDropdowns="showDropdowns"
    class="custom-date-range-picker"
    :ranges="ranges"
    :locale-data="localeAction"
    @toggle="toggleDateTimePicker"
    @update="updateValues"
  >
    <template #input="picker">
      {{ picker | date(viewFormat) }}
    </template>
  </date-range-picker>
</template>

<script>
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import moment from "moment";
import CommonUIControl from "@/helper/CommonUIControl";
import { cloneDeep } from "lodash";
import { $t } from "@/plugins/i18n";

const formatDate = (val, format) => {
  return val ? moment(val).format(format) : format;
};

export default {
  name: "SlotsDemo",
  props: {
    opens: {
      type: String,
      default: "center",
    },
    timePicker: {
      type: Boolean,
      default: true,
    },
    timePicker24Hour: {
      type: Boolean,
      default: true,
    },
    showDropdowns: {
      type: Boolean,
      default: true,
    },
    value: {
      type: Object,
    },
    genRanges: {
      type: Function,
      default: () => [],
    },
    maxDurationStartEnd: {
      type: Number, //  days
    },
    viewFormat: {
      type: String,
      default: "HH:mm DD/MM/YYYY",
    },
  },
  components: { DateRangePicker },
  data() {
    return {
      messages: {
        en: {
          daysOfWeek: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
          monthNames: [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ],
        },
        ko: {
          daysOfWeek: ["일", "월", "화", "수", "목", "금", "토"],
          monthNames: [
            "1월",
            "2월",
            "3월",
            "4월",
            "5월",
            "6월",
            "7월",
            "8월",
            "9월",
            "10월",
            "11월",
            "12월",
          ],
        },
      },
      dateRange: { startDate: null, endDate: null },
      ranges: null,
      localeKey: 0, // force re-render on language change
    };
  },
  computed: {
    localeAction() {
      return {
        applyLabel: $t("common.base.button.apply"),
        cancelLabel: $t("common.base.button.cancel"),
        daysOfWeek: this.messages[this.$i18n.locale].daysOfWeek,
        monthNames: this.messages[this.$i18n.locale].monthNames,
      };
    },
  },
  filters: {
    date(val, format) {
      if (!val?.startDate && !val?.endDate)
        return $t("common.base.label.time_duration");

      return `${formatDate(val?.startDate, format)} - ${formatDate(
        val?.endDate,
        format,
      )}`;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        this.dateRange = this.value;
      },
    },
    "$i18n.locale"() {
      // Trigger updates when the language changes
      this.localeKey += 1;
    },
  },
  methods: {
    updateValues(value) {
      if (this.maxDurationStartEnd) {
        const startDate = moment(value.startDate);
        const endDate = moment(value.endDate);
        var durationInMinutes = moment
          .duration(endDate.diff(startDate))
          .asDays();

        if (durationInMinutes > this.maxDurationStartEnd) {
          CommonUIControl.ShowWarningToast(
            $t("common.base.toast.time_duration_warning_max", {
              maxDuration: this.maxDurationStartEnd,
            }),
          );
          this.dateRange = cloneDeep(this.value);

          return;
        }
      }

      this.$emit("input", value);
    },
    toggleDateTimePicker(isOpen) {
      if (isOpen) {
        if (this.genRanges) this.ranges = this.genRanges();
      }
    },
  },
  mounted() {
    if (this.genRanges) this.ranges = this.genRanges();
  },
};
</script>

<style lang="scss">
.custom-date-range-picker {
  width: 100%;
  > div {
    width: 100%;
    border: 1px solid rgb(229, 231, 235) !important;
    border-radius: 4px;
    font-size: 14px;
    color: var(--gray2);
    min-height: 40px;
    padding-top: 8px !important;
  }
}
</style>
